import { z } from 'zod';
/**
 * Per-tool progress payload schemas. Six tools emit `tool_progress` SSE
 * events with tool-specific payloads; this module defines the formal Zod
 * shape per tool so consumers can validate beyond the generic `payload:
 * Record<string, unknown>` carried on `SSEToolProgressEvent`.
 *
 * Tools that DO emit progress: run_sensitivity_2d, market_data_enrichment,
 * apply_market_defaults, run_sensitivity (1D), goal_seek, compare_scenarios.
 *
 * Tools that DO NOT emit progress: set_inputs, calculate, get_values,
 * get_dependencies, validate_input, export_sensitivity_tab, generate_excel,
 * get_model_info, set_table_inputs, accept_defaults, propose_plan.
 *
 * `generateExcelProgressSchema` is `@deprecated` — Phase 4 of
 * agent-ux-optimization retired the `generate_excel` paint surface (the
 * `<ActionButtonCluster>` Generate button was removed in Phase 3, and the
 * tool now runs server-side only with no user-perceivable surface). Schema
 * stays exported for back-compat / future rollback. See backlog item
 * `xlsx-tool-retirement-or-admin-scoping`.
 */
// --- run_sensitivity_2d ---
export const sens2dProgressSchema = z.discriminatedUnion('phase', [
    z.object({
        phase: z.literal('grid_start'),
        x_axis: z.string(),
        y_axis: z.string(),
        dimensions: z.tuple([
            z.number().int().positive(),
            z.number().int().positive(),
        ]),
    }),
    z.object({
        phase: z.literal('cell_computed'),
        x_idx: z.number().int().nonnegative(),
        y_idx: z.number().int().nonnegative(),
        /** Output values keyed by metric name. */
        output: z.record(z.string(), z.unknown()),
    }),
    z.object({ phase: z.literal('grid_complete') }),
]);
// --- market_data_enrichment ---
export const marketDataTier = z.enum([
    'web_search',
    'fred',
    'rentcast',
    'hud_fmr',
    'cache',
    'static_defaults',
]);
export const marketDataProgressSchema = z.discriminatedUnion('phase', [
    z.object({
        phase: z.literal('tier_start'),
        tier: marketDataTier,
        series_id: z.string().optional(),
    }),
    z.object({
        phase: z.literal('tier_hit'),
        tier: marketDataTier,
        source: z.string().optional(),
        value: z.number().optional(),
        source_url: z.string().optional(),
    }),
    z.object({
        phase: z.literal('tier_skip'),
        tier: marketDataTier,
        reason: z.string(),
    }),
]);
// --- generate_excel ---
/** @deprecated Retired by agent-ux-optimization Phase 4 — `generate_excel`
 * runs server-side only; no user-perceivable paint surface. Schema kept
 * for back-compat. See backlog `xlsx-tool-retirement-or-admin-scoping`. */
export const generateExcelProgressSchema = z.discriminatedUnion('phase', [
    z.object({ phase: z.literal('sheets_start'), sheets: z.array(z.string()) }),
    z.object({
        phase: z.literal('sheet_serialized'),
        sheet: z.string(),
        cells: z.number().int().nonnegative(),
    }),
    z.object({
        phase: z.literal('formulas_resolved'),
        resolved: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
    z.object({
        phase: z.literal('xlsx_zipped'),
        size_bytes: z.number().int().nonnegative(),
    }),
    z.object({ phase: z.literal('gcs_uploaded'), url: z.string() }),
]);
// --- run_sensitivity (1D) ---
export const sens1dProgressSchema = z.discriminatedUnion('phase', [
    z.object({
        phase: z.literal('steps_planned'),
        count: z.number().int().positive(),
    }),
    z.object({
        phase: z.literal('step_computed'),
        x: z.number(),
        /** Output values keyed by metric name. */
        output: z.record(z.string(), z.unknown()),
    }),
]);
// --- apply_market_defaults ---
export const applyMarketDefaultsProgressSchema = z.discriminatedUnion('phase', [
    z.object({
        phase: z.literal('lookup_start'),
        deal_type: z.string(),
        location: z.string().optional(),
    }),
    z.object({
        phase: z.literal('defaults_applied'),
        count: z.number().int().nonnegative(),
        names: z.array(z.string()),
    }),
    z.object({
        phase: z.literal('table_write'),
        table: z.string(),
        cells_written: z.number().int().nonnegative(),
        row_labels: z.array(z.string()),
    }),
]);
// --- goal_seek ---
export const goalSeekProgressEventSchema = z.discriminatedUnion('phase', [
    z.object({
        phase: z.literal('goal_seek_started'),
        target: z.number(),
        output_name: z.string(),
        input_name: z.string(),
        max_iterations: z.number().int().positive(),
    }),
    z.object({
        phase: z.literal('iteration_completed'),
        iteration: z.number().int().nonnegative(),
        /** Current input mid-point under test. */
        current_value: z.number(),
        /** Current output value at mid-point. */
        current_output: z.number(),
        /** |current_output - target| — drives the renderer's distance display. */
        distance_from_target: z.number(),
        /** Post-iteration `[lo, hi]` search range. */
        search_range: z.tuple([z.number(), z.number()]),
    }),
    z.object({
        phase: z.literal('goal_seek_converged'),
        iterations: z.number().int().positive(),
        final_input: z.number(),
        final_output: z.number(),
    }),
    z.object({
        phase: z.literal('goal_seek_no_convergence'),
        // `iterations` is `nonnegative()` (NOT `positive()`) so the
        // out-of-range early-return path — which emits `goal_seek_no_convergence`
        // before any binary-search iteration runs — can pass `iterations: 0`.
        // The budget-exhausted path emits a positive iteration count.
        iterations: z.number().int().nonnegative(),
        last_input: z.number(),
        last_output: z.number(),
        last_error: z.number(),
    }),
]);
// --- compare_scenarios ---
export const compareScenariosProgressEventSchema = z.discriminatedUnion('phase', [
    z.object({
        phase: z.literal('scenarios_started'),
        kind: z.enum(['matrix', 'compact_3axis']),
        scenario_count: z.number().int().positive(),
        output_names: z.array(z.string()),
    }),
    z.object({
        phase: z.literal('scenario_computed'),
        scenario_index: z.number().int().nonnegative(),
        scenario_name: z.string(),
        outputs: z.record(z.string(), z.number()),
        /** Present from scenario_index >= 1 (base is index 0; deltas are vs base). */
        deltas_vs_base: z.record(z.string(), z.number()).optional(),
    }),
    z.object({ phase: z.literal('scenarios_complete') }),
]);
